import { zExt } from "$root/third-party-integrations/zod";
import { zAgGridColumnState } from "$root/ui-lib/table/col-state-schema";
import type { ColumnState } from "ag-grid-community";
import z from "zod";
import { CompositionFirstLevel, type CompositionGroupBy, DEFAULT_COMPOSITION_GROUP_BY } from "../../state";

/**
 * The composition tree's "allocation preference": the group-by selection (allocation vs holdings + the
 * classification grouped by) plus the grid column layout (width/order/pinned/sort). Persisted into the opaque
 * {@link LabGridConfig.preferences} bag the backend stores as-is (documented to hold "column widths, expand
 * state, group-by uuid, sort"). `LabGridConfig.projection` — concept columns shown inside the tree — is not
 * driven by the FE yet; it lives outside this bag and is round-tripped verbatim by the lab-session adapter.
 */
export type LabGridPreferences = { groupBy: CompositionGroupBy; columns: ColumnState[] };

const zGroupBy = z.object({
	firstLevel: z.nativeEnum(CompositionFirstLevel),
	secondLevel: z.string(),
});

const labGridPreferencesSchema = zExt.versioned(
	1,
	z.object({
		// Optional for forward/backward compatibility: a bag written before either field existed (or by a
		// client that only persists one) parses cleanly and falls back to defaults below. `.catch` isolates
		// a malformed field so it can't discard its valid sibling — a bad `groupBy` still keeps `columns`,
		// and vice versa.
		groupBy: zGroupBy.optional().catch(undefined),
		columns: z.array(zAgGridColumnState).optional().catch(undefined),
	}),
);

/** Parses the opaque `LabGridConfig.preferences` bag into context state, defaulting anything absent/invalid. */
export function parseGridPreferences(prefs: unknown): LabGridPreferences {
	const result = labGridPreferencesSchema.safeParse(prefs);
	if (!result.success) {
		return { groupBy: DEFAULT_COMPOSITION_GROUP_BY, columns: [] };
	}
	return {
		groupBy: result.data.data.groupBy ?? DEFAULT_COMPOSITION_GROUP_BY,
		columns: result.data.data.columns ?? [],
	};
}

/** Reconstructs the persisted/wire `LabGridConfig.preferences` bag from context state. */
export function toWireGridPreferences(grid: LabGridPreferences): object {
	return { version: 1, data: { groupBy: grid.groupBy, columns: grid.columns } };
}
